import { gql, useQuery, type WatchQueryFetchPolicy } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import CAD from '@assets/images/country-flags/Canada.svg';
import USD from '@assets/images/country-flags/US.svg';
import MULITCURRENCY from '@assets/images/custom/multiCurrency.svg';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { getSubAccountName } from '@interfaces/subAccount';
import { type AccountTypes, CurrencyCodes } from '@onevesthq/ov-enums';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { useScreenWidth } from '@utils/mediaQueryUtils';
import { STATE_CHANGES_FIELD } from '@utils/reusableGraphQLFields';
import dayjs from 'dayjs';
import { Box, Typography } from 'hephaestus/web';
import { groupBy, orderBy } from 'lodash-es';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';

const SUB_ACCOUNT_FIELDS = gql`
  fragment SubAccountFields on SubAccount {
    state
    ${STATE_CHANGES_FIELD}
    financialProduct { translatedName { ${TRANSLATED_STRING_FIELDS} } }
    goal { name type }
    account {
      id
      type
      user { id dateOfBirth physicalAddress { jurisdiction province country } }
      availableCurrencies
      baseCurrency
      custodianAccountNumber
    }
    id
    createdAt
    completedAt
    statistics {
      currency
      marketValueCents
      availableFundsToWithdrawCents
      originalCurrencies {
        currency
        availableFundsToWithdrawCents
        moneyAvailableCents
        pendingWithdrawCents
      }
    }
  }
`;

const FETCH_SUB_ACCOUNTS = gql`
  query fetchSubAccounts($input: FetchSubAccountsInput!) {
    fetchSubAccounts(input: $input) {
      subAccounts { ...SubAccountFields }
      totalCount
    }
  }
  ${SUB_ACCOUNT_FIELDS}
`;

const FETCH_SUB_ACCOUNT = gql`
  query fetchSubAccount($id: ObjectID!) {
    fetchSubAccount(subAccountId: $id) {
      subAccount { ...SubAccountFields }
    }
  }
  ${SUB_ACCOUNT_FIELDS}
`;

const PLACEHOLDER_SX = {
  fontStyle: 'italic',
  color: 'text.secondary',
} as const;

type SubAccountSelectProp = {
  userId?: string;
  label: string;
  onSubAccountSelect: (subAccount: SubAccount | undefined) => void;
  selectedSubAccount?: any | undefined;
  accountToExclude?: AccountTypes[];
  subAccountsToExclude?: string[];
  sx?: any;
  size?: 'small' | 'medium';
  showValue?: boolean;
  showCurrencyAvatar?: boolean;
  filter?: {
    userId?: string;
    subAccountId?: string;
    accountId?: string;
    goalId?: string;
    clientGroupId?: string;
  };
  onBlur?: () => void;
  error?: boolean;
  filterNotAvailableWithdraw?: boolean;
  excludedIds?: string[];
  id?: string;
  name?: string;
  dataTestId?: string;
  omitAny?: boolean;
  placeholder?: string;
  displayCustodianAccountNumber?: boolean;
  selectFirstSubAccountOnly?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  filterSubAccount?: (subAccount: SubAccount) => boolean;
};

const SubAccountMenuItem = ({
  subAccount,
  showAvatar,
  displayCustodianAccountNumber,
}: {
  subAccount: SubAccount;
  showAvatar: boolean;
  displayCustodianAccountNumber: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const accountName = getSubAccountName(
    translateBackendString,
    subAccount,
    false,
    displayCustodianAccountNumber,
  );

  const currency = subAccount?.account?.baseCurrency;

  const getCurrencyImage = () => {
    if ((subAccount?.account?.availableCurrencies?.length ?? 0) > 1) {
      return MULITCURRENCY;
    }

    switch (currency) {
      case CurrencyCodes.CAD:
        return CAD;
      case CurrencyCodes.USD:
        return USD;
      default:
        return null;
    }
  };

  const currencyImage = getCurrencyImage();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {showAvatar && currencyImage && (
        <Box
          sx={{
            mr: 1,
            height: 20,
            width: 20,
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <img
            alt={currency || 'currency'}
            src={currencyImage}
            style={{ objectFit: 'cover', height: '100%' }}
          />
        </Box>
      )}
      <Typography
        variant='bodyMedium'
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...(showAvatar ? { width: 'calc(100% - 28px)' } : {}),
          fontWeight: 500,
        }}
      >
        {accountName}
      </Typography>
    </Box>
  );
};

export const SubAccountSelect = ({
  userId,
  label,
  selectedSubAccount,
  sx = {},
  size,
  filter,
  error,
  accountToExclude,
  subAccountsToExclude,
  omitAny = false,
  placeholder,
  filterNotAvailableWithdraw = false,
  id,
  name,
  dataTestId = 'sub-account-select',
  onBlur,
  onSubAccountSelect,
  showCurrencyAvatar = false,
  fetchPolicy = 'cache-and-network',
  displayCustodianAccountNumber = false,
  selectFirstSubAccountOnly = false,
  filterSubAccount,
}: SubAccountSelectProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['components']);
  const screenWidth = useScreenWidth();
  const shouldUseSingleQuery = !!filter?.subAccountId;

  const { data: singleData, loading: singleLoading } = useQuery(
    FETCH_SUB_ACCOUNT,
    {
      variables: { id: filter?.subAccountId },
      skip: !shouldUseSingleQuery,
      fetchPolicy,
    },
  );

  const { data: multipleData, loading: multipleLoading } = useQuery(
    FETCH_SUB_ACCOUNTS,
    {
      variables: {
        input: { filter: filter || { userId }, pagination: { perPage: 100 } },
      },
      skip: shouldUseSingleQuery,
      fetchPolicy,
    },
  );

  useEffect(() => {
    const singleSubAccount = singleData?.fetchSubAccount?.subAccount;
    if (shouldUseSingleQuery && singleSubAccount) {
      onSubAccountSelect(singleSubAccount);
    }
  }, [shouldUseSingleQuery, singleData, onSubAccountSelect]);

  const subAccounts: SubAccount[] = shouldUseSingleQuery
    ? singleData?.fetchSubAccount?.subAccount
      ? [singleData.fetchSubAccount.subAccount]
      : []
    : (multipleData?.fetchSubAccounts?.subAccounts ?? []);

  const subAccountsByAccountId = groupBy(
    orderBy(subAccounts, (s) => dayjs(s.createdAt).unix()),
    (s) => s.account.id,
  );

  const filteredSubAccounts = subAccounts.filter((subAccount: SubAccount) => {
    if (subAccount.state === 'INACTIVE') return false;
    if (accountToExclude?.includes(subAccount.account.type)) return false;
    if (subAccountsToExclude?.includes(subAccount.id)) return false;
    if (
      filterNotAvailableWithdraw &&
      !subAccount.statistics?.availableFundsToWithdrawCents
    )
      return false;
    if (
      selectFirstSubAccountOnly &&
      subAccount.id !== subAccountsByAccountId[subAccount.account.id]?.at(0)?.id
    )
      return false;
    if (filterSubAccount && !filterSubAccount(subAccount)) return false;
    return true;
  });

  const handleSubAccountSelect = (event: any) => {
    if (!event.target.value || event.target.value === 'ANY') {
      onSubAccountSelect(undefined);
      return;
    }

    const selectedAccount = filteredSubAccounts.find(
      (item: SubAccount) => item.id === event.target.value,
    );
    if (selectedAccount) onSubAccountSelect(selectedAccount);
  };

  const selectProps = omitAny &&
    placeholder && {
      displayEmpty: true,
      renderValue: (selectedId: string) => {
        if (!selectedId) {
          return (
            <Typography
              component='span'
              variant='bodyLarge'
              sx={PLACEHOLDER_SX}
            >
              {placeholder}
            </Typography>
          );
        }
        const subAccount = filteredSubAccounts.find((s) => s.id === selectedId);
        return subAccount
          ? getSubAccountName(
              translateBackendString,
              subAccount,
              false,
              displayCustodianAccountNumber,
            )
          : '';
      },
    };

  return (
    <SelectField
      key={screenWidth}
      id={id}
      name={name}
      onChange={handleSubAccountSelect}
      label={label}
      testId={dataTestId}
      fullWidth
      sx={sx}
      size={size}
      value={selectedSubAccount?.id ?? (omitAny ? '' : 'ANY')}
      onBlur={onBlur}
      error={error}
      disabled={singleLoading || multipleLoading || shouldUseSingleQuery}
      SelectProps={selectProps}
    >
      {!omitAny && (
        <MenuItem value='ANY' data-testid='sub-account-item-any'>
          {t('components:any')}
        </MenuItem>
      )}
      {filteredSubAccounts.map((subAccount: SubAccount) => (
        <MenuItem
          key={subAccount.id}
          data-testid={`sub-account-item-${getSubAccountName(translateBackendString, subAccount)}`}
          value={subAccount.id}
          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {showCurrencyAvatar ? (
            <SubAccountMenuItem
              subAccount={subAccount}
              showAvatar={showCurrencyAvatar}
              displayCustodianAccountNumber={displayCustodianAccountNumber}
            />
          ) : (
            getSubAccountName(
              translateBackendString,
              subAccount as SubAccount,
              false,
              displayCustodianAccountNumber,
            )
          )}
        </MenuItem>
      ))}
    </SelectField>
  );
};
